import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { GlobalLoadingIndicatorService } from '../../../services/global-loading-indicator/global-loading-indicator.service';
import {
  AUTH_REFRESH_FOLLOW_UP_DELAY_MS,
  AuthRefreshTemporarilyUnavailableError,
} from './auth-refresh.models';

@Injectable({ providedIn: 'root' })
export class AuthRefreshNavigationRecoveryService {
  private readonly router = inject(Router);
  private readonly loadingIndicator = inject(GlobalLoadingIndicatorService);
  private readonly destroyRef = inject(DestroyRef);

  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private recovering = false;

  constructor() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.finishRecovery());
  }

  handle(event: NavigationError): void {
    if (!(event.error instanceof AuthRefreshTemporarilyUnavailableError)) {
      return;
    }

    if (!this.recovering) {
      this.recovering = true;
      this.loadingIndicator.showLoading();
    }

    this.cancelRetry();
    this.retryTimeoutId = setTimeout(() => {
      this.retryTimeoutId = null;
      void this.router.navigateByUrl(event.url);
    }, AUTH_REFRESH_FOLLOW_UP_DELAY_MS);
  }

  private finishRecovery(): void {
    if (!this.recovering) return;

    this.cancelRetry();
    this.recovering = false;
    this.loadingIndicator.hideLoading();
  }

  private cancelRetry(): void {
    if (this.retryTimeoutId === null) return;

    clearTimeout(this.retryTimeoutId);
    this.retryTimeoutId = null;
  }
}
